/* A device id that does not need a secure context.
 *
 * `crypto.randomUUID()` is only defined in a SECURE CONTEXT — HTTPS or
 * localhost. This app is reached over plain HTTP at least as often as over the
 * Tailscale HTTPS name: `http://openplotter.local:3000/gennetay-dash/` is what
 * you get from a laptop on the boat's own network, and that is not secure by
 * the browser's definition.
 *
 * Calling it unguarded is fatal rather than degraded. Both call sites run in a
 * class constructor at module load, so `undefined is not a function` takes the
 * whole app down before it paints — which is exactly what the parity harness
 * caught on its first run against the pi: the legacy dashboard rendering every
 * value and the new one rendering nothing at all.
 *
 * `crypto.getRandomValues` has no such restriction and is available everywhere
 * this runs, so the fallback is still a real random UUID rather than a
 * timestamp with a counter on it.
 */

export function randomUUID(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();

  if (typeof c?.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6]! & 0x0f) | 0x40; // version 4
    b[8] = (b[8]! & 0x3f) | 0x80; // variant 1
    const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }

  /* No WebCrypto at all. This identifies one tablet to one server, so
   * Math.random is adequate — and anything is better than throwing. */
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
